import { zodResolver } from '@hookform/resolvers/zod';
import { CustomApiClient, useListContext } from '@jobcost-monorepo/crud';
import {
  IListTimesheetFetchParams,
  ITimesheet,
} from '@jobcost-monorepo/crud/dto/timesheet';
import dayjs from '@jobcost-monorepo/dayjs-config';
import { useTranslation } from '@jobcost-monorepo/i18n';
import { Alert, Icon, Loader } from '@jobcost-monorepo/ui';
import React, {
  ReactElement,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { util } from 'zod';
import ReplicateFormBody from './Body';
import ReplicateFormButtons from './Buttons';
import { replicationFormValidationSchema } from './replicateFormValidationSchema';
import ReplicateFormTitle from './Title';
import Omit = util.Omit;

export interface IReplicateForm extends ITimesheet {
  days: number[];
  startDate: string;
  endDate: string;
}

interface IReplicateFormProps {
  setReplicateOpen: (value: SetStateAction<boolean>) => void;
}

const ReplicateForm = ({
  setReplicateOpen,
}: IReplicateFormProps): ReactElement => {
  const { t } = useTranslation();
  const [showPopup, setShowPopup] = React.useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const { fetchDataList, params, repository } = useListContext<
    ITimesheet,
    IListTimesheetFetchParams
  >();

  const methods = useForm<IReplicateForm>({
    mode: 'onSubmit',
    defaultValues: {
      days: [1, 2, 3, 4, 5],
      startDate: dayjs.utc().startOf('month').format('YYYY-MM-DD'),
      endDate: dayjs.utc().endOf('month').format('YYYY-MM-DD'),
      operationDate: dayjs.utc().startOf('month').toDate(), //Necessario per l'hook, ricerca commesse e ordini
    },
    resolver: zodResolver(replicationFormValidationSchema),
  });

  const { reset, handleSubmit, watch, setValue } = methods;
  const startDate = watch('startDate');

  const onSubmit: SubmitHandler<IReplicateForm> = (data) => {
    handleReplicate(data).then();
  };

  const handleCloseFormAndFetch = async () => {
    await fetchDataList({
      ...params,
    } as IListTimesheetFetchParams);
  };

  const handleCloseForm = () => {
    reset();
    setReplicateOpen(false);
  };

  const handleChangeCheckbox = (value: number, checked: boolean) => {
    const days = methods.getValues('days');
    if (checked) {
      return days
        ? methods.setValue('days', [...days, value])
        : methods.setValue('days', [value]);
    }
    return methods.setValue(
      'days',
      days.filter((day: number) => day !== value),
    );
  };

  const handleReplicate = async (data: IReplicateForm) => {
    setLoading(true);
    try {
      // Il campo operationDate viene rimosso in quando non necessario al submit, serve solo per l'hook
      const { operationDate, ...dataToSubmit } = data;
      const customApiClient = new CustomApiClient();
      await customApiClient.create<Omit<IReplicateForm, 'operationDate'>>(
        'timesheets/replicate',
        dataToSubmit,
      );
      await handleCloseFormAndFetch();
      setShowPopup(true);
    } catch (error: any) {
      console.log(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    setValue('operationDate', dayjs.utc(startDate).toDate());
  }, [startDate]);

  useEffect(() => {
    if (showPopup) {
      setTimeout(() => {
        setShowPopup(false);
      }, 3000);
    }
  }, [showPopup]);

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="size-full flex flex-col"
      >
        <div className="flex justify-between items-center content-center">
          <ReplicateFormTitle />
          <div
            onClick={handleCloseForm}
            className="bg-slate-200 text-slate-900 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-100 rounded-2xl p-1"
          >
            <Icon
              iconName="cancel"
              className="hover:opacity-50 hover:cursor-pointer fill-black stroke-black dark:fill-white dark:stroke-white"
            />
          </div>
        </div>
        <div className="flex flex-grow overflow-hidden flex-col border h-full justify-between py-4 mt-4 rounded-lg border-purple70">
          <Loader showLoader={loading} />
          <ReplicateFormBody handleChangeCheckbox={handleChangeCheckbox} />
          <ReplicateFormButtons handleCloseForm={handleCloseForm} />
        </div>
        {repository.hook && <repository.hook />}
      </form>
      {showPopup && <Alert type="success" message={t('recordSaved')} />}
    </FormProvider>
  );
};

export default ReplicateForm;
